/**
 * Generates quantum noise based on various quantum metrics
 */
export class QuantumNoiseGenerator {
  /**
   * Generate quantum noise based on quantum state and other metrics
   * @param {Object} params Quantum metrics and state parameters
   * @returns {number} Noise multiplier between 0.5 and 1.5
   */
  static generate(params) {
    const {
      coherenceVar,
      entanglementVar,
      resonanceVar,
      particleVar = 0.5,
      cosmicVar = 0.5,
      qualityFactor = 0.5
    } = params;

    const timestamp = Date.now() * 0.001; // Convert to seconds

    // Primary quantum oscillation
    const primaryWave = Math.sin(timestamp * resonanceVar * 2) * coherenceVar;
    
    // Secondary particle-influenced wave
    const particleWave = particleVar * Math.cos(timestamp * 1.5) * 0.5;
    
    // Cosmic interference pattern
    const cosmicWave = cosmicVar * Math.sin(timestamp * 0.7) * 0.3;
    
    // Quantum entanglement modulation
    const entanglementMod = 1 + (entanglementVar * Math.sin(timestamp) * 0.4);
    
    // Quality influence on noise amplitude
    const qualityMod = 1 + (qualityFactor * 0.3);
    
    // Combine all components with dynamic scaling
    const combinedNoise = (primaryWave + particleWave + cosmicWave) * 
                         entanglementMod * qualityMod;
    
    // Ensure noise stays within reasonable bounds (-0.5 to 0.5 range)
    const boundedNoise = Math.max(-0.5, Math.min(0.5, combinedNoise));
    
    // Return final multiplier (0.5 to 1.5 range)
    return 1 + boundedNoise;
  }

  /**
   * Generate a time-based phase shift
   * @param {number} resonance Base resonance value
   * @returns {number} Phase shift value
   */
  static generatePhaseShift(resonance) {
    const timestamp = Date.now() * 0.001;
    return Math.sin(timestamp * resonance) * Math.PI;
  }

  /**
   * Generate quantum interference pattern
   * @param {number} coherence Coherence value
   * @param {number} entanglement Entanglement value
   * @returns {number} Interference multiplier
   */
  static generateInterference(coherence, entanglement) {
    const timestamp = Date.now() * 0.001;
    const interference = Math.sin(timestamp * coherence) * 
                        Math.cos(timestamp * entanglement);
    return 1 + (interference * 0.3);
  }
}